/**
 * TemplateEditorPage — ★ Fase 8.T: editor MATRICI a pagina intera.
 * Sostituisce il vecchio dialog: più spazio, import da PDF, campi {{chiave}}.
 * Solo amministrazione. Rotte: /app/contracts/matrice/nuova · /app/contracts/matrice/:templateId
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  ArrowLeft, Loader2, PlusCircle, Braces, FileUp, Save, FileSignature,
  Pencil, Check, X, Paperclip, FileText,
} from 'lucide-react';
import RichEmailEditor from '../components/RichEmailEditor';
import { extractFieldKeys } from '../components/contracts/contractUtils';

const EMPTY_CONTENT = '<h2>Titolo del contratto</h2><p>Il sottoscritto {{nome_cliente}} …</p>';

// ★ Fase 12 — allegati predefiniti della matrice
const MAX_TPL_ATTACHMENTS = 5;
const MAX_TPL_ATT_SIZE = 5 * 1024 * 1024; // 5 MB
const TPL_ATT_MIMES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

function fmtAttSize(att) {
  const bytes = att.size ?? (att.size_b64 ? Math.round(att.size_b64 * 0.75) : 0);
  if (!bytes) return '';
  if (bytes > 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export default function TemplateEditorPage() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const isEdit = !!templateId;

  const [loading, setLoading] = useState(isEdit);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState(EMPTY_CONTENT);
  const [labels, setLabels] = useState({}); // key -> {label, required}
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [fieldPopover, setFieldPopover] = useState(false);
  const [newFieldKey, setNewFieldKey] = useState('');
  const [renaming, setRenaming] = useState(null); // { key, value }
  // ★ Fase 12 — allegati predefiniti: esistenti {id,name,mime,size_b64} + nuovi {name,mime,b64,size}
  const [tplAtts, setTplAtts] = useState([]);
  const pdfInputRef = useRef(null);
  const attInputRef = useRef(null);
  const editorApiRef = useRef(null);

  useEffect(() => {
    if (!isEdit) return;
    api.get('/contracts/templates')
      .then((r) => {
        const tpl = (r.data || []).find((t) => t.id === templateId);
        if (!tpl) {
          toast.error('Matrice non trovata');
          navigate('/app/contracts');
          return;
        }
        setName(tpl.name || '');
        setDescription(tpl.description || '');
        setContent(tpl.content_html || EMPTY_CONTENT);
        const map = {};
        (tpl.fields || []).forEach((f) => { map[f.key] = { label: f.label, required: f.required !== false }; });
        setLabels(map);
        setTplAtts(tpl.attachments || []);
      })
      .catch(() => { toast.error('Errore caricamento matrice'); navigate('/app/contracts'); })
      .finally(() => setLoading(false));
  }, [isEdit, templateId, navigate]);

  const keys = useMemo(() => extractFieldKeys(content), [content]);

  const normalizeKey = (raw) =>
    (raw || '').trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');

  const insertField = () => {
    const key = normalizeKey(newFieldKey);
    if (!key) { toast.error('Inserisci il nome del campo (es. nome_cliente)'); return; }
    // ★ Fase 8.U — inserisce nel punto in cui si trova(va) il cursore
    const atCursor = editorApiRef.current?.insertText(`{{${key}}}`);
    if (!atCursor) {
      setContent((c) => `${c}<p>{{${key}}}</p>`);
    }
    setNewFieldKey('');
    setFieldPopover(false);
    toast.success(
      atCursor
        ? `Campo {{${key}}} inserito nel punto del cursore.`
        : `Campo {{${key}}} aggiunto in fondo: spostalo dove serve.`,
    );
  };

  // ★ Fase 8.U — rinomina un campo ovunque nel testo ({{vecchio}} → {{nuovo}})
  const renameField = () => {
    if (!renaming) return;
    const oldKey = renaming.key;
    const newKey = normalizeKey(renaming.value);
    if (!newKey) { toast.error('Nome campo non valido (usa lettere, numeri, _)'); return; }
    if (newKey === oldKey) { setRenaming(null); return; }
    if (keys.includes(newKey)) { toast.error(`Il campo {{${newKey}}} esiste già nel testo.`); return; }
    const re = new RegExp(`\\{\\{\\s*${oldKey}\\s*\\}\\}`, 'g');
    const occurrences = (content.match(re) || []).length;
    setContent(content.replace(re, `{{${newKey}}}`));
    setLabels((s) => {
      const next = { ...s };
      if (next[oldKey]) { next[newKey] = next[oldKey]; delete next[oldKey]; }
      return next;
    });
    setRenaming(null);
    toast.success(
      `Campo rinominato in {{${newKey}}}`,
      { description: `${occurrences} occorrenz${occurrences === 1 ? 'a aggiornata' : 'e aggiornate'} nel testo.` },
    );
  };

  const importPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permette di ricaricare lo stesso file
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Seleziona un file PDF.');
      return;
    }
    if (file.size > 15 * 1024 * 1024) { toast.error('PDF troppo grande (max 15 MB).'); return; }
    const hasContent = content && content !== EMPTY_CONTENT && content.replace(/<[^>]+>/g, '').trim().length > 30;
    if (hasContent && !window.confirm('Il testo attuale verrà sostituito con quello estratto dal PDF. Continuare?')) return;
    setImporting(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result).split(',')[1]);
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      const { data } = await api.post('/contracts/templates/import-pdf', { pdf_b64: b64 }, { timeout: 120000 });
      setContent(data.content_html);
      if (!name.trim()) setName(file.name.replace(/\.pdf$/i, '').replace(/[_-]+/g, ' '));
      toast.success(`PDF importato: ${data.pages} pagine`, {
        description: data.detected_fields > 0
          ? `${data.detected_fields} spazi da compilare rilevati automaticamente ({{campo_N}}): rinominali come preferisci.`
          : 'Nessuno spazio rilevato: inserisci i campi {{campo}} dove servono.',
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore durante l\'import del PDF');
    } finally {
      setImporting(false);
    }
  };

  // ★ Fase 12 — upload allegati predefiniti della matrice
  const onPickTplAtts = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (tplAtts.length + files.length > MAX_TPL_ATTACHMENTS) {
      toast.error(`Massimo ${MAX_TPL_ATTACHMENTS} allegati predefiniti per matrice.`);
      return;
    }
    for (const file of files) {
      if (!TPL_ATT_MIMES.includes(file.type)) {
        toast.error(`«${file.name}»: formato non supportato (PDF o immagini).`);
        continue;
      }
      if (file.size > MAX_TPL_ATT_SIZE) {
        toast.error(`«${file.name}» troppo grande (max 5 MB).`);
        continue;
      }
      try {
        const b64 = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(String(fr.result).split(',')[1]);
          fr.onerror = reject;
          fr.readAsDataURL(file);
        });
        setTplAtts((s) => [...s, { name: file.name, mime: file.type, b64, size: file.size }]);
      } catch {
        toast.error(`Impossibile leggere «${file.name}».`);
      }
    }
  };

  const save = async () => {
    if (name.trim().length < 2) { toast.error('Dai un nome alla matrice'); return; }
    if (keys.length === 0) { toast.error('Inserisci almeno un campo editabile {{campo}}'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        content_html: content,
        fields: keys.map((k) => ({
          key: k,
          label: labels[k]?.label || '',
          required: labels[k]?.required !== false,
        })),
        // ★ Fase 12 — allegati predefiniti: esistenti per id, nuovi con contenuto
        attachments: tplAtts.map((a) => (a.id
          ? { id: a.id, name: a.name, mime: a.mime }
          : { name: a.name, mime: a.mime, content_b64: a.b64 })),
      };
      if (isEdit) await api.put(`/contracts/templates/${templateId}`, payload);
      else await api.post('/contracts/templates', payload);
      toast.success(isEdit ? 'Matrice aggiornata' : 'Matrice creata');
      navigate('/app/contracts');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio matrice');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-5xl mx-auto space-y-4">
        <Skeleton className="h-10 w-1/2" /><Skeleton className="h-64" /><Skeleton className="h-10" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="template-editor-page">
      {/* Barra superiore */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" className="h-9 shrink-0" onClick={() => navigate('/app/contracts')} data-testid="tpl-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Contratti
          </Button>
          <h1 className="font-serif text-xl md:text-2xl text-[hsl(var(--primary))] flex items-center gap-2 truncate">
            <FileSignature className="w-6 h-6 shrink-0" />
            {isEdit ? 'Modifica matrice' : 'Nuova matrice'}
          </h1>
        </div>
        <Button onClick={save} disabled={saving} data-testid="tpl-save-btn" className="bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {isEdit ? 'Salva modifiche' : 'Crea matrice'}
        </Button>
      </div>

      {/* Dati matrice */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wide">Nome matrice *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Proposta di prenotazione" className="mt-1" data-testid="tpl-name-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Descrizione</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Uso interno" className="mt-1" data-testid="tpl-desc-input" />
          </div>
        </CardContent>
      </Card>

      {/* Editor */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Label className="text-xs uppercase tracking-wide">Testo del contratto *</Label>
            <div className="flex items-center gap-2">
              <input
                ref={pdfInputRef} type="file" accept="application/pdf,.pdf" className="hidden"
                onChange={importPdf} data-testid="tpl-pdf-input"
              />
              <Button
                size="sm" variant="outline" className="h-8 text-xs"
                onClick={() => pdfInputRef.current?.click()}
                disabled={importing}
                data-testid="tpl-import-pdf-btn"
              >
                {importing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileUp className="w-3.5 h-3.5 mr-1" />}
                {importing ? 'Import in corso…' : 'Importa da PDF'}
              </Button>
              <Popover open={fieldPopover} onOpenChange={setFieldPopover}>
                <PopoverTrigger asChild>
                  <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="tpl-insert-field-btn">
                    <Braces className="w-3.5 h-3.5 mr-1" /> Inserisci campo
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72" align="end">
                  <Label className="text-xs">Nome campo (senza spazi)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={newFieldKey}
                      onChange={(e) => setNewFieldKey(e.target.value)}
                      placeholder="es. importo"
                      className="h-8 text-sm"
                      data-testid="tpl-new-field-input"
                      onKeyDown={(e) => e.key === 'Enter' && insertField()}
                    />
                    <Button size="sm" className="h-8" onClick={insertField} data-testid="tpl-new-field-add">
                      <PlusCircle className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Il campo viene inserito <b>nel punto in cui si trova il cursore</b> nel testo.
                    Puoi anche scrivere direttamente {'{{campo}}'}.
                  </p>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Scrivi o importa il testo e inserisci gli spazi da compilare nel formato{' '}
            <code className="bg-[var(--surface-2)] px-1 rounded">{'{{nome_campo}}'}</code>:
            verranno compilati al momento dell&apos;invio.
          </p>
          <RichEmailEditor
            ref={editorApiRef}
            value={content}
            onChange={setContent}
            placeholder="Scrivi qui il testo del contratto…"
            minHeight="52vh"
            testId="tpl-editor"
            highlightFields
          />
        </CardContent>
      </Card>

      {/* Campi rilevati */}
      <Card>
        <CardContent className="p-4">
          <Label className="text-xs uppercase tracking-wide">
            Campi editabili rilevati ({keys.length})
          </Label>
          {keys.length === 0 ? (
            <p className="text-xs text-muted-foreground mt-1 italic">
              Nessun campo: inserisci almeno un {'{{campo}}'} nel testo.
            </p>
          ) : (
            <>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Clicca sul nome di un campo (matita) per <b>rinominarlo ovunque</b> nel testo.
              </p>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="tpl-fields-list">
                {keys.map((k) => (
                  <div key={k} className="flex items-center gap-2 rounded-md border border-border bg-[var(--surface-2)]/60 p-2">
                    {renaming?.key === k ? (
                      <div className="flex items-center gap-1 shrink-0">
                        <Input
                          value={renaming.value}
                          onChange={(e) => setRenaming({ key: k, value: e.target.value })}
                          className="h-8 w-40 text-xs font-mono"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') renameField();
                            if (e.key === 'Escape') setRenaming(null);
                          }}
                          data-testid={`tpl-field-rename-input-${k}`}
                        />
                        <Button size="sm" className="h-8 w-8 p-0" onClick={renameField} data-testid={`tpl-field-rename-confirm-${k}`} aria-label="Conferma rinomina">
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setRenaming(null)} aria-label="Annulla rinomina">
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="flex items-center gap-1 shrink-0 group rounded hover:bg-white/70 px-0.5 py-0.5"
                        onClick={() => setRenaming({ key: k, value: k })}
                        title="Rinomina campo ovunque nel testo"
                        data-testid={`tpl-field-rename-${k}`}
                      >
                        <code className="text-[11px] bg-white border px-1.5 py-0.5 rounded">{'{{'}{k}{'}}'}</code>
                        <Pencil className="w-3 h-3 text-muted-foreground opacity-50 group-hover:opacity-100" />
                      </button>
                    )}
                    <Input
                      value={labels[k]?.label ?? ''}
                      onChange={(e) => setLabels((s) => ({ ...s, [k]: { ...(s[k] || { required: true }), label: e.target.value } }))}
                      placeholder={k.replace(/_/g, ' ')}
                      className="h-8 text-sm flex-1"
                      data-testid={`tpl-field-label-${k}`}
                    />
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Switch
                        checked={labels[k]?.required !== false}
                        onCheckedChange={(v) => setLabels((s) => ({ ...s, [k]: { ...(s[k] || {}), required: v } }))}
                        data-testid={`tpl-field-required-${k}`}
                      />
                      <span className="text-[10px] text-muted-foreground">obbligat.</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ★ Fase 12 — Allegati predefiniti della matrice */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <Label className="text-xs uppercase tracking-wide flex items-center gap-1.5">
            <Paperclip className="w-3.5 h-3.5" />
            Allegati predefiniti ({tplAtts.length}/{MAX_TPL_ATTACHMENTS})
          </Label>
          <p className="text-[11px] text-muted-foreground">
            Questi allegati vengono <b>sempre inclusi</b> in ogni contratto inviato da questa
            matrice e <b>non sono rimovibili</b> al momento dell&apos;invio (il mittente potrà
            solo aggiungerne altri).
          </p>
          <div className="space-y-2" data-testid="tpl-attachments-list">
            {tplAtts.map((a, i) => (
              <div
                key={a.id || `${a.name}-${i}`}
                className="flex items-center gap-2 rounded-md border border-border bg-[var(--surface-2)]/60 px-2.5 py-1.5"
                data-testid={`tpl-attachment-${i}`}
              >
                <FileText className="w-4 h-4 text-[hsl(var(--primary))] shrink-0" />
                <span className="text-xs truncate flex-1">{a.name}</span>
                <span className="text-[10px] text-muted-foreground shrink-0">{fmtAttSize(a)}</span>
                {a.id && (
                  <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 shrink-0">salvato</span>
                )}
                <button
                  type="button"
                  className="text-red-500 hover:text-red-700 shrink-0"
                  onClick={() => setTplAtts((s) => s.filter((_, j) => j !== i))}
                  data-testid={`tpl-attachment-remove-${i}`}
                  aria-label={`Rimuovi ${a.name}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {tplAtts.length < MAX_TPL_ATTACHMENTS && (
              <label className="flex items-center gap-2 rounded-md border-2 border-dashed border-border hover:border-[hsl(var(--primary))]/40 px-3 py-2.5 cursor-pointer text-xs text-muted-foreground">
                <Paperclip className="w-4 h-4" />
                Aggiungi allegati predefiniti (es. Privacy, Statuto SPV…) — PDF o immagini, max 5 MB
                <input
                  ref={attInputRef}
                  type="file" multiple className="hidden"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  onChange={onPickTplAtts}
                  data-testid="tpl-attachments-input"
                />
              </label>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Barra inferiore */}
      <div className="flex justify-end gap-2 pb-6">
        <Button variant="ghost" onClick={() => navigate('/app/contracts')} disabled={saving}>Annulla</Button>
        <Button onClick={save} disabled={saving} data-testid="tpl-save-btn-bottom" className="bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {isEdit ? 'Salva modifiche' : 'Crea matrice'}
        </Button>
      </div>
    </div>
  );
}
